import { useEffect, useState } from "react";
import { ApolloProvider } from "@apollo/client/react";
import { Check, Minus, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/common/providers/i18n-provider";
import { STRIPE_CONFIG } from "@/config/stripe";
import { useMounted } from "@/hooks/use-mounted";
import { getBeancountClient } from "@/common/apollo/beancount-client";
import {
  useCreateSubscriptionSession,
  useIsLoggedIn,
  useSubscriptionStatus,
  useUserProfile,
  type UserProfileQuery,
} from "@/components/pricing/subscription-hooks";
import {
  buildTiers,
  FEATURES,
  USAGE_FEATURES,
  tierHighlights,
  tierLabel,
  type TierIndex,
  type FeatureValue,
  type PaidTierKey,
  type Tier,
} from "@/components/pricing/pricing-data";
import { formatDollars, formatPrice } from "@/lib/price-format";
import {
  CONTACT_URL,
  MANAGE_SUBSCRIPTION_URL,
  SIGN_UP_PRICING,
} from "@/config/product-links";
import { productUrl } from "@/lib/product-attribution";
import {
  trackCheckoutStart,
  trackCtaClick,
  trackPricingView,
} from "@/lib/pricing-analytics";
import { PricingFaq } from "@/components/pricing/PricingFaq";

// --- Presentation helpers ---

function ctaClass(popular: boolean) {
  return cn("pricing-cta", popular && "pricing-cta-primary");
}

function FeatureCell({ value }: { value: FeatureValue }) {
  const { t } = useTranslation();
  if (typeof value === "boolean") {
    return (
      <span className={value ? "pricing-included" : "pricing-excluded"}>
        {value ? (
          <Check size={19} aria-hidden="true" />
        ) : (
          <Minus size={17} aria-hidden="true" />
        )}
        <span className="sr-only">
          {t(value ? "pricing.included" : "pricing.notIncluded")}
        </span>
      </span>
    );
  }
  return <>{t(value)}</>;
}

function yearlyBillingRequested(): boolean {
  return (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("billing") === "yearly"
  );
}

function pricingSignupUrl(yearly: boolean): string {
  const signupUrl = new URL(SIGN_UP_PRICING);
  if (yearly) {
    // Keep the selected cadence through sign-up so returning visitors do not
    // silently fall back to the monthly checkout price.
    signupUrl.searchParams.set("next", "/pricing?billing=yearly");
  }
  return productUrl(signupUrl.toString(), "pricing_signup_first");
}

/** The yearly argument reflects the live toggle, including authenticated checkout. */
type RenderCta = (tier: Tier, yearly: boolean) => React.ReactNode;

function PricingComparisonTable({
  tiers,
  renderCta,
  initialYearly = false,
}: {
  tiers: Tier[];
  renderCta: RenderCta;
  initialYearly?: boolean;
}) {
  const { t } = useTranslation();
  const [yearly, setYearly] = useState(initialYearly);

  return (
    <>
      <div
        className="pricing-billing"
        role="group"
        aria-label={t("pricing.monthly") + " / " + t("pricing.yearly")}
      >
        <button
          type="button"
          aria-pressed={!yearly}
          aria-controls="pricing-comparison-grid"
          onClick={() => setYearly(false)}
        >
          {t("pricing.monthly")}
        </button>
        <button
          type="button"
          aria-pressed={yearly}
          aria-controls="pricing-comparison-grid"
          onClick={() => setYearly(true)}
        >
          {t("pricing.yearly")}
        </button>
      </div>
      <p className="pricing-currency">{t("pricing.currencyNote")}</p>

      <div id="pricing-comparison-grid">
        <div className="pricing-plans">
          {tiers.map((tier, index) => (
            <article
              key={tier.name}
              data-tier={tier.name}
              aria-labelledby={"pricing-plan-" + index}
              className={cn(
                "pricing-plan",
                tier.popular && "pricing-plan-popular",
              )}
            >
              <div className="pricing-plan-heading">
                {tier.popular ? (
                  <span className="pricing-popular">{t("common.popular")}</span>
                ) : null}
                <h2 id={"pricing-plan-" + index}>{tierLabel(tier, t)}</h2>
                <p>{t(tier.subtitleI18nKey)}</p>
              </div>
              <div className="pricing-plan-price">
                {tier.monthlyPrice !== null && tier.yearlyPrice !== null ? (
                  <>
                    <div className="pricing-amount">
                      <span className="pricing-dollar">$</span>
                      <span className="pricing-number">
                        {formatPrice(
                          yearly ? tier.yearlyPrice : tier.monthlyPrice,
                        )}
                      </span>
                      <span className="pricing-cadence">
                        {t("common.pricingPerMonth")}
                      </span>
                    </div>
                    {tier.annualPrice !== null && tier.annualPrice > 0 ? (
                      <>
                        {!yearly ? (
                          <p className="pricing-annual">
                            {t("pricing.billedAnnually", {
                              price: formatDollars(tier.yearlyPrice),
                            })}
                          </p>
                        ) : null}
                        {/* The annual charge shows in both states: the
                            server-rendered page is the monthly one, and the
                            Product JSON-LD declares this amount as an offer
                            (`check-representation-parity`, fact-missing). */}
                        <p className="pricing-annual">
                          {t("pricing.yearlyTotal", {
                            price: formatDollars(tier.annualPrice),
                          })}
                        </p>
                      </>
                    ) : null}
                  </>
                ) : (
                  <p className="pricing-contact-price">
                    {t("footer.link.contact")}
                  </p>
                )}
              </div>
              {renderCta(tier, yearly)}
              <dl className="pricing-highlights">
                {tierHighlights(index as TierIndex).map((highlight) => (
                  <div key={highlight.nameKey}>
                    <dt>{t(highlight.nameKey)}</dt>
                    <dd>{t(highlight.value)}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
        <p className="pricing-reassurance">
          <ShieldCheck size={20} aria-hidden="true" />
          {t("pricing.cancelAnytime")}
        </p>

        <section className="pricing-comparison">
          <div className="pricing-comparison-heading">
            <h2 id="pricing-comparison-title">{t("pricing.comparePlans")}</h2>
            <p className="pricing-scroll-hint">{t("pricing.compareHint")}</p>
          </div>
          {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- This overflow region needs keyboard focus so every plan column can be scrolled into view. */}
          <div
            className="pricing-table-scroll"
            role="region"
            aria-labelledby="pricing-comparison-title"
            tabIndex={0}
          >
            <table>
              <caption className="sr-only">{t("pricing.comparePlans")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("pricing.features")}</th>
                  {tiers.map((tier) => (
                    <th
                      key={tier.name}
                      scope="col"
                      className={
                        tier.popular ? "pricing-table-popular" : undefined
                      }
                    >
                      {tierLabel(tier, t)}
                    </th>
                  ))}
                </tr>
              </thead>
              {[
                { title: "pricing.usage", rows: USAGE_FEATURES },
                { title: "pricing.features", rows: FEATURES },
              ].map((group) => (
                <tbody key={group.title}>
                  <tr className="pricing-table-group">
                    <th scope="rowgroup" colSpan={tiers.length + 1}>
                      {t(group.title)}
                    </th>
                  </tr>
                  {group.rows.map((feature) => (
                    <tr key={feature.labelKey}>
                      <th scope="row">{t(feature.labelKey)}</th>
                      {tiers.map((tier, index) => (
                        <td
                          key={tier.name}
                          className={
                            tier.popular ? "pricing-table-popular" : undefined
                          }
                        >
                          <FeatureCell value={feature.values[index]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
          {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
        </section>
      </div>
      <p className="pricing-support">
        {t("pricing.anyQuestion")}{" "}
        <a href={productUrl(CONTACT_URL, "pricing_enterprise")}>
          {t("pricing.contactSupport")}
        </a>
      </p>
    </>
  );
}

// --- Static (SSR / no-JS) variant ---

function StaticPricingTable() {
  const { t } = useTranslation();
  const tiers = buildTiers(STRIPE_CONFIG);
  const renderCta: RenderCta = (tier) => (
    <a href={tier.cta.href} className={ctaClass(tier.popular)}>
      {t(tier.cta.labelKey)}
    </a>
  );
  return <PricingComparisonTable tiers={tiers} renderCta={renderCta} />;
}

// --- Interactive (client-only) variant ---

function InteractivePricingTable() {
  const { isLoggedIn } = useIsLoggedIn();
  const {
    data: userProfile,
    loading: userLoading,
    error: userError,
  } = useUserProfile();
  const { hasActiveSubscription } = useSubscriptionStatus();
  const { createSubscriptionSession, loading: creatingSession } =
    useCreateSubscriptionSession();

  const tiers = buildTiers(STRIPE_CONFIG);

  async function handleTierCheckout(tierKey: PaidTierKey, yearly: boolean) {
    if (!userProfile?.userProfile?.id) return;
    const { priceId, amount } =
      STRIPE_CONFIG.tiers[tierKey][yearly ? "yearly" : "monthly"];

    trackCheckoutStart(amount);

    const resp = await createSubscriptionSession({
      variables: { clientId: STRIPE_CONFIG.clientId, priceId },
    });
    const sessionUrl = resp.data?.createSubscriptionSession?.sessionUrl;
    if (sessionUrl) {
      window.location.href = sessionUrl;
    }
  }

  const renderCta: RenderCta = (tier, yearly) => (
    <DynamicCta
      tier={tier}
      yearly={yearly}
      isLoggedIn={isLoggedIn}
      userError={!!userError}
      userLoading={userLoading}
      userProfile={userProfile}
      hasActiveSubscription={hasActiveSubscription}
      creatingSession={creatingSession}
      onCheckout={handleTierCheckout}
    />
  );

  return (
    <PricingComparisonTable
      tiers={tiers}
      renderCta={renderCta}
      initialYearly={yearlyBillingRequested()}
    />
  );
}

function DynamicCta({
  tier,
  yearly,
  isLoggedIn,
  userError,
  userLoading,
  userProfile,
  hasActiveSubscription,
  creatingSession,
  onCheckout,
}: {
  tier: Tier;
  yearly: boolean;
  isLoggedIn: boolean;
  userError: boolean;
  userLoading: boolean;
  userProfile: UserProfileQuery | undefined;
  hasActiveSubscription: boolean;
  creatingSession: boolean;
  onCheckout: (tierKey: PaidTierKey, yearly: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();

  // Free / Enterprise: plain links with analytics.
  if (!tier.tierKey) {
    const label =
      tier.name === "Free" ? "start_for_free" : "contact_enterprise";
    return (
      <a
        href={tier.cta.href}
        className={ctaClass(tier.popular)}
        onClick={() => trackCtaClick(label)}
      >
        {t(tier.cta.labelKey)}
      </a>
    );
  }

  // Not logged in (or profile unavailable): send to sign-up first.
  if (!isLoggedIn || userError || (!userLoading && !userProfile?.userProfile)) {
    return (
      <a
        href={pricingSignupUrl(yearly)}
        className={ctaClass(tier.popular)}
        onClick={() => trackCtaClick("sign_up_first")}
      >
        {t("pricing.signUpFirst")}
      </a>
    );
  }

  // Already subscribed: send them to the dashboard's subscription settings —
  // a dead disabled button here left plan changes with no path forward.
  if (hasActiveSubscription) {
    return (
      <a
        href={productUrl(MANAGE_SUBSCRIPTION_URL, "pricing_manage")}
        className={ctaClass(tier.popular)}
        onClick={() => trackCtaClick("manage_subscription")}
      >
        {t("pricing.manageSubscription")}
      </a>
    );
  }

  // Logged in, no active subscription: start checkout.
  return (
    <button
      type="button"
      disabled={creatingSession}
      onClick={(e) => {
        e.preventDefault();
        trackCtaClick(`subscribe_${tier.tierKey}`);
        void onCheckout(tier.tierKey!, yearly);
      }}
      className={cn(
        ctaClass(tier.popular),
        creatingSession && "opacity-50 cursor-not-allowed",
      )}
    >
      {creatingSession ? t("pricing.loading") : t("common.subscribe")}
    </button>
  );
}

// --- Client-only switch ---

function PricingTableSwitch() {
  const mounted = useMounted();
  if (!mounted) {
    return <StaticPricingTable />;
  }
  return (
    <ApolloProvider client={getBeancountClient()}>
      <InteractivePricingTable />
    </ApolloProvider>
  );
}

// --- Exported section ---

export function PricingSection() {
  useEffect(() => {
    trackPricingView(document?.referrer || "direct");
  }, []);

  return (
    <section className="pricing-content pricing-container">
      <PricingTableSwitch />
      <PricingFaq />
    </section>
  );
}
